import { supabase } from '../../shared/supabase/supabaseClient';
import type {
  ProductCatalogEventRow,
  ProductCatalogEventType,
  ProductCatalogHistoryFilters,
} from '../domain/catalogTypes';

/**
 * ADR-017 Fase 4: SQLite eliminado como path de datos operacional.
 * El historial de eventos de catálogo ahora lee directo de Supabase
 * (`product_catalog_events`), con RLS `org_select` que filtra por org del usuario.
 */

type CatalogEventDbRow = {
  id: string;
  product_id: string;
  event_type: string;
  summary: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  performed_by_user_id: string | null;
  origin_device_id: string | null;
};

function mapRow(row: CatalogEventDbRow): ProductCatalogEventRow {
  return {
    id: row.id,
    productId: row.product_id,
    eventType: row.event_type as ProductCatalogEventType,
    summary: row.summary,
    payload:
      row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
        ? (row.payload as Record<string, unknown>)
        : {},
    createdAt: row.created_at,
    performedByUserId: row.performed_by_user_id ?? undefined,
    originDeviceId: row.origin_device_id ?? undefined,
  };
}

const SELECT_COLUMNS =
  'id, product_id, event_type, summary, payload, created_at, performed_by_user_id, origin_device_id';

export const getProductCatalogHistory = async (
  productId: string,
  limit = 20,
  offset = 0,
  filters?: ProductCatalogHistoryFilters,
): Promise<ProductCatalogEventRow[]> => {
  let query = supabase
    .from('product_catalog_events')
    .select(SELECT_COLUMNS)
    .eq('product_id', productId);
  if (filters?.fromCreatedAt) query = query.gte('created_at', filters.fromCreatedAt);
  if (filters?.toCreatedAt) query = query.lte('created_at', filters.toCreatedAt);
  if (filters?.eventType && filters.eventType !== 'all') query = query.eq('event_type', filters.eventType);

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw new Error(error.message);
  return (data ?? []).map(mapRow);
};

export const getProductCatalogHistoryForExport = async (
  productId: string,
  filters?: ProductCatalogHistoryFilters,
  maxRows = 10_000,
): Promise<ProductCatalogEventRow[]> => {
  let query = supabase
    .from('product_catalog_events')
    .select(SELECT_COLUMNS)
    .eq('product_id', productId);
  if (filters?.fromCreatedAt) query = query.gte('created_at', filters.fromCreatedAt);
  if (filters?.toCreatedAt) query = query.lte('created_at', filters.toCreatedAt);
  if (filters?.eventType && filters.eventType !== 'all') query = query.eq('event_type', filters.eventType);

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(maxRows);

  if (error) throw new Error(error.message);
  return (data ?? []).map(mapRow);
};
